import { useCallback } from "react";
import dayjs from "dayjs";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";

export type MisalignedBoundary = "start" | "end" | "both";

export type MisalignedCalendarSlotDTO = {
  title: string;
  start: string;
  end: string;
  misalignedBoundary: MisalignedBoundary;
  calendarName?: string;
};

export type MisalignedWorkingHoursDTO = {
  days: string[];
  startMinute: number;
  endMinute: number;
  misalignedBoundary: MisalignedBoundary;
};

export type CloserMisalignedSlotsDTO = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  slots: MisalignedCalendarSlotDTO[];
  workingHours: MisalignedWorkingHoursDTO[];
};

export function useGetHubSpotCloserMisalignedSlots() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<CloserMisalignedSlotsDTO[]>();

  const getMisalignedSlotsCall = useCallback(
    async ({
      fromIncl,
      toIncl,
      includeSecondaryCalendars,
    }: {
      fromIncl: Date;
      toIncl: Date;
      includeSecondaryCalendars: boolean;
    }) => {
      const { data } = await axiosInstance.get<CloserMisalignedSlotsDTO[]>(
        `/closer-availability/misaligned-slots?fromIncl=${encodeURIComponent(
          dayjs(fromIncl).format(),
        )}&toIncl=${encodeURIComponent(
          dayjs(toIncl).format(),
        )}&includeSecondaryCalendars=${includeSecondaryCalendars}`,
      );

      if (!Array.isArray(data)) {
        console.error("Unexpected misaligned slots response:", data);
        throw new Error("Unexpected response format from HubSpot adapter");
      }

      return data;
    },
    [axiosInstance],
  );

  const getHubSpotCloserMisalignedSlots = useCallback(
    async (input: {
      fromIncl: Date;
      toIncl: Date;
      includeSecondaryCalendars: boolean;
    }) => {
      return await withLoading(() => getMisalignedSlotsCall(input));
    },
    [withLoading, getMisalignedSlotsCall],
  );

  return {
    getHubSpotCloserMisalignedSlots,
    loading,
  };
}
